import { useEffect, useMemo, useState } from 'react'
import {
  fetchFeaturedStoreProducts,
  fetchNewArrivalStoreProducts,
  fetchStoreProductsByIds,
} from '@/services/products'
import { fetchBanners } from '@/services/banners'
import { fetchCategories } from '@/services/categories'
import { fetchHomeNotes } from '@/services/notes'
import { fetchInstagramReels } from '@/services/instagramReels'
import { fetchBestSellerProductIds } from '@/services/store/ordersApi'
import { useStoreGate } from '@/contexts/StoreGateContext'
import type { Product, Banner, Category, InstagramReel, Note } from '@/types'
import {
  HomeCategories,
  HomeFragranceNotes,
  HomeBanners,
  HomeProductShowcase,
  HomeFeaturedVideos,
  HomeFoundersDesk,
  HomeWhyChooseUs,
  type ProductShowcaseBlock,
} from '@/store/components/home'
import {
  HomeCategoriesSkeleton,
  HomeFragranceNotesSkeleton,
  HomeBannersSkeleton,
  HomeProductSectionSkeleton,
} from '@/store/components/StoreSkeletons'

export function HomePage() {
  const { settings } = useStoreGate()

  const [featured, setFeatured] = useState<Product[]>([])
  const [bestSellers, setBestSellers] = useState<Product[]>([])
  const [newArrivals, setNewArrivals] = useState<Product[]>([])
  const [banners, setBanners] = useState<Banner[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [notes, setNotes] = useState<Note[]>([])
  const [notesHasMore, setNotesHasMore] = useState(false)
  const [instagramReels, setInstagramReels] = useState<InstagramReel[]>([])
  const [bannersReady, setBannersReady] = useState(false)
  const [productsReady, setProductsReady] = useState(false)
  const [categoriesReady, setCategoriesReady] = useState(false)
  const [notesReady, setNotesReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    void fetchBanners()
      .then((items) => {
        if (cancelled) return
        setBanners(items.filter((x) => x.enabled))
      })
      .finally(() => {
        if (!cancelled) setBannersReady(true)
      })

    void Promise.allSettled([
      fetchFeaturedStoreProducts(4),
      fetchNewArrivalStoreProducts(4),
    ])
      .then(([featuredResult, newArrivalsResult]) => {
        if (cancelled) return

        setFeatured(
          featuredResult.status === 'fulfilled' ? featuredResult.value : []
        )
        setNewArrivals(
          newArrivalsResult.status === 'fulfilled' ? newArrivalsResult.value : []
        )
      })
      .finally(() => {
        if (!cancelled) setProductsReady(true)
      })

    void fetchBestSellerProductIds(4)
      .then(async (bestSellerIds) => {
        if (cancelled || bestSellerIds.length === 0) return
        try {
          setBestSellers(await fetchStoreProductsByIds(bestSellerIds))
        } catch {
          if (!cancelled) setBestSellers([])
        }
      })

    void fetchCategories()
      .then((items) => {
        if (cancelled) return
        setCategories(items)
      })
      .finally(() => {
        if (!cancelled) setCategoriesReady(true)
      })

    void fetchHomeNotes()
      .then(({ notes: homeNotes, hasMore }) => {
        if (cancelled) return
        setNotes(homeNotes)
        setNotesHasMore(hasMore)
      })
      .finally(() => {
        if (!cancelled) setNotesReady(true)
      })

    void fetchInstagramReels()
      .then((items) => {
        if (cancelled) return
        setInstagramReels(items.filter((x) => x.enabled))
      })

    return () => {
      cancelled = true
    }
  }, [])

  const productSections = useMemo<ProductShowcaseBlock[]>(
    () => [
      {
        id: 'featured',
        title: 'Featured Fragrances',
        subtitle: 'Editorial picks from our master perfumers.',
        href: '/shop?featured=1',
        products: featured,
      },
      {
        id: 'best-sellers',
        title: 'Best Sellers',
        subtitle: 'The fragrances our community returns to again and again.',
        href: '/shop',
        products: bestSellers,
      },
      {
        id: 'new-arrivals',
        title: 'New Arrivals',
        subtitle: 'Fresh compositions and limited releases.',
        href: '/shop?sort=new',
        linkLabel: 'See new',
        products: newArrivals,
      },
    ],
    [featured, bestSellers, newArrivals]
  )

  return (
    <div className="overflow-x-hidden">
      {categoriesReady ? (
        <HomeCategories categories={categories} />
      ) : (
        <HomeCategoriesSkeleton />
      )}

      {!bannersReady ? (
        <HomeBannersSkeleton />
      ) : (
        <HomeBanners banners={banners} />
      )}

      {notesReady ? (
        <HomeFragranceNotes notes={notes} showViewAll={notesHasMore} />
      ) : (
        <HomeFragranceNotesSkeleton />
      )}

      {productsReady ? (
        <HomeProductShowcase sections={productSections} />
      ) : (
        <>
          <HomeProductSectionSkeleton />
          <HomeProductSectionSkeleton />
        </>
      )}

      <HomeFeaturedVideos reels={instagramReels} instagram={settings?.instagram} />

      <HomeWhyChooseUs />

      <HomeFoundersDesk />
    </div>
  )
}
